import { useRootStore } from "@/store/root";
import { isCovomoTariffManagerTheme } from "@/utils/tariffManagerSearch";
import { isCovomoPortalSearchTheme } from "@/utils/portalSearchParams";
import { isConfigFlagDisabled, isConfigFlagEnabled } from "@/utils/configFlag";

const COOKIE_CONSENT_NAME = "covomo-cookie-consent";
const LIVE_CHAT_CONSENT_CATEGORY = "functionality_storage";

/** Embedded configurator themes (tariff manager, portal search) show no live chat. */
function isTariffManagerThemeContext(theme?: unknown): boolean {
  if (
    theme !== undefined &&
    (isCovomoTariffManagerTheme(theme) || isCovomoPortalSearchTheme(theme))
  ) {
    return true;
  }

  if (typeof window !== "undefined" && window.__covomoIsConsentFreeTheme?.()) {
    return true;
  }

  try {
    const storeTheme = useRootStore().theme;
    return (
      isCovomoTariffManagerTheme(storeTheme) ||
      isCovomoPortalSearchTheme(storeTheme)
    );
  } catch {
    return false;
  }
}

export function isLiveChatFeatureEnabled(theme?: unknown): boolean {
  if (isTariffManagerThemeContext(theme)) {
    return false;
  }
  // Match SSR/meta_tags Freshchat bootstrap: enabled unless explicitly turned off.
  return isConfigFlagEnabled(
    window?.rechnerCustomConfig?.enable_live_chat,
    true,
  );
}

/** Re-run server-injected Freshchat init after functionality cookie consent. */
export function bootstrapFreshchatAfterConsent(): void {
  if (!isLiveChatFeatureEnabled() || !hasLiveChatConsent()) {
    return;
  }

  // Server initFreshChat calls fcWidget.init(); skip when already ready.
  if (isFreshchatAvailable()) {
    return;
  }

  const initFreshChat = (window as Window & { initFreshChat?: () => void })
    .initFreshChat;
  if (typeof initFreshChat !== "function") {
    return;
  }

  // widget.js is injected on window.load; Vue may mount earlier.
  if (!window.fcWidget) {
    return;
  }

  try {
    initFreshChat();
  } catch {
    // Race with async widget.js — LiveChatLauncher polling will retry.
  }
}

export function hasLiveChatConsent(): boolean {
  if (typeof document === "undefined") return false;

  const cookiesDisabled = isConfigFlagDisabled(
    window?.rechnerCustomConfig?.enable_cookies_disclaimer,
  );
  if (cookiesDisabled) return true;

  const consent = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${COOKIE_CONSENT_NAME}=`))
    ?.slice(COOKIE_CONSENT_NAME.length + 1);

  if (!consent) return false;
  return consent.split(",").includes(LIVE_CHAT_CONSENT_CATEGORY);
}

export function isFreshchatAvailable(): boolean {
  return (
    typeof window !== "undefined" && typeof window.fcWidget?.open === "function"
  );
}

export function hideFreshchatLauncher(): void {
  window.fcWidget?.setConfig?.({
    config: {
      chatButton: {
        visibility: "hidden",
      },
    },
  });
}

/** Close and hide Freshchat when embedded in tariff-manager backoffice. */
export function disableFreshchatForTariffManagerTheme(theme?: unknown): void {
  if (!isTariffManagerThemeContext(theme)) {
    return;
  }

  hideFreshchatLauncher();
  if (window.fcWidget?.isOpen?.() && window.fcWidget.close) {
    window.fcWidget.close();
  }
}

export function openFreshchat(): boolean {
  if (!isLiveChatFeatureEnabled()) return false;
  if (!isFreshchatAvailable()) return false;
  window.fcWidget?.open();
  return true;
}

export function closeFreshchat(): void {
  if (window.fcWidget?.isOpen?.() && window.fcWidget.close) {
    window.fcWidget.close();
  }
}

export function toggleFreshchat(): boolean {
  if (!isLiveChatFeatureEnabled()) return false;
  if (window.fcWidget?.isOpen?.()) {
    closeFreshchat();
    return true;
  }
  return openFreshchat();
}

export function initFreshchatCustomLauncher(): () => void {
  if (!isLiveChatFeatureEnabled()) {
    disableFreshchatForTariffManagerTheme();
    return () => {
      /* Freshchat disabled for tariff-manager theme */
    };
  }

  const registerHide = () => {
    hideFreshchatLauncher();
    window.fcWidget?.on?.("widget:loaded", hideFreshchatLauncher);
  };

  registerHide();

  let attempts = 0;
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (window.fcWidget) {
      registerHide();
    }
    if (attempts >= 40) {
      window.clearInterval(intervalId);
    }
  }, 500);

  return () => window.clearInterval(intervalId);
}
